// What a deal says it did.
//
// "Deal more from this board" used to answer in a boolean, so the screen could
// only ever say "Nothing more to deal from this board" — the same eight words
// whether four tickets had landed or the board had genuinely run out. A punter
// pressing a button that adds things to a list a long way further up the feed
// has no way of knowing it worked otherwise: the tickets land in kick-off
// order, which is rarely at the bottom where the button is.
//
// So the deal answers `{ added, slots, exhausted, offered }` — the human names
// of the slots it filled, and the ones that OFFERED something the feed was
// already holding — and this file is the only place those names become a
// sentence. Pure, imports `format.js` and nothing else, and every sentence in
// it is pinned in `deal-copy.test.js`: a plural that does not agree or a list
// with a trailing comma is a thing you find by reading it, not by running it.

import { TIER_LABEL } from './format.js';

/**
 * The human name of a Classic tier — "Low risk", "Outrageous".
 *
 * Falls back to "Ticket", the same fallback `history-view.js` uses, so a ticket
 * from a retired tier still reads as a ticket rather than as an id.
 */
export const tierName = (tier) => TIER_LABEL[tier] ?? 'Ticket';

/**
 * The human name of a timeline slot key, as `slotOf` (lib/timeline.js) writes
 * them: `classic-four-ways#low` is a Classic TIER, anything else is a whole
 * strategy and is named from `names` — the store's id → name map.
 */
export function slotName(slot, names = {}) {
  const key = String(slot ?? '');
  const hash = key.indexOf('#');
  if (hash >= 0) return tierName(key.slice(hash + 1));
  return names[key] ?? 'Ticket';
}

/** In order, once each: a pad deals two Low tickets and says "Low risk" once. */
const unique = (list) => [...new Set((list ?? []).filter(Boolean))];

/**
 * "Dealt 3 new tickets · Medium risk, High risk", and "Dealt 1 new ticket ·
 * Low risk" — the count first because it is the answer to "did that work",
 * then where they landed, because the feed is in kick-off order and the new
 * ones are scattered through it.
 *
 * Empty string when nothing landed: that case is `exhaustedLine`'s.
 */
export function dealtLine(result) {
  // `?? {}` rather than a destructuring default: a default only answers for
  // `undefined`, and a deal that never ran is a null on every call site here.
  const { added = 0, slots = [] } = result ?? {};
  const n = Number(added) || 0;
  if (n <= 0) return '';
  const head = `Dealt ${n} new ticket${n === 1 ? '' : 's'}`;
  const named = unique(slots);
  return named.length ? `${head} · ${named.join(', ')}` : head;
}

/**
 * Why nothing landed, when the reason is that the board has run out of ways to
 * print something DIFFERENT from what the punter is already holding.
 *
 * EXHAUSTED IS NOT EMPTY. A slot in this list DID offer a ticket — it was the
 * same legs the feed already has, or a ticket inside one of them — so it was
 * not dealt twice. That is the one dead end worth naming, because it is the one
 * a punter can do nothing about and would otherwise read as a broken button.
 *
 * "DIFFERENT" IS THE WHOLE SENTENCE NOW, and it is what changed with the caps.
 * Deal more used to stop at a slot's cap, so the honest answer was often "that
 * tier is full"; it no longer has one, and the only thing that can hold it back
 * is the board itself having no other multi to make. So the words say so.
 *
 * Every slot the deal offered is exhausted → the board itself is done, and
 * naming four tiers to say so is a list nobody needs. Some of them → name
 * those, so the punter knows the rest of the feed is still moving.
 *
 * Empty string when nothing was even offered: that is a different dead end
 * (a board too thin to print anything at all) and the screen has its own words.
 */
export function exhaustedLine(result) {
  const { exhausted = [], offered = [] } = result ?? {};
  const named = unique(exhausted);
  if (!named.length) return '';
  const all = unique(offered).length > 0 && named.length >= unique(offered).length;
  if (all) return 'Every different multi today’s prices allow is already on the feed';
  // One name reads as a sentence about that tier; two or more have to be a
  // list first, or "Every different Low risk, Medium risk multi" is a tier
  // nobody has ever heard of.
  if (named.length === 1) {
    return `Every different ${named[0]} multi today’s prices allow is already on the feed`;
  }
  return `${named.join(', ')}: every different multi today’s prices allow is already on the feed`;
}

/**
 * The one sentence a deal is worth saying, or '' when it has nothing to say and
 * the screen should fall back to its own words.
 */
export function dealLine(result) {
  return dealtLine(result) || exhaustedLine(result);
}
